import React, { useState, useEffect } from 'react';
import { api, type TopProduct, type CategorySales } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface MonthlyReport {
  month: string;
  total_sales: number;
  number_of_transactions: number;
  daily_average: number;
}

interface PeriodComparison {
  current_period: {
    total_sales: number;
    transactions: number;
    average_sale: number;
  };
  previous_period: {
    total_sales: number;
    transactions: number;
    average_sale: number;
  };
  growth: {
    sales_growth: number;
    transactions_growth: number;
    average_growth: number;
  };
}

const Reports: React.FC = () => {
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [comparison, setComparison] = useState<PeriodComparison | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportType, setReportType] = useState<'monthly' | 'comparison'>('monthly');
  const [loading, setLoading] = useState(false);
  const [periodDays, setPeriodDays] = useState(30);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/sales/monthly-report/${selectedYear}/${selectedMonth}`);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - periodDays);
      
      const previousStartDate = new Date(startDate);
      previousStartDate.setDate(previousStartDate.getDate() - periodDays);
      
      const [currentSales, previousSales] = await Promise.all([
        api.get(`/sales?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`),
        api.get(`/sales?start_date=${previousStartDate.toISOString().split('T')[0]}&end_date=${startDate.toISOString().split('T')[0]}`)
      ]);
      
      const currentTotal = currentSales.data.reduce((sum: number, s: any) => sum + s.total_amount, 0);
      const currentCount = currentSales.data.length;
      const currentAvg = currentCount > 0 ? currentTotal / currentCount : 0;
      
      const previousTotal = previousSales.data.reduce((sum: number, s: any) => sum + s.total_amount, 0);
      const previousCount = previousSales.data.length;
      const previousAvg = previousCount > 0 ? previousTotal / previousCount : 0;
      
      const salesGrowth = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal * 100) : 0;
      const transGrowth = previousCount > 0 ? ((currentCount - previousCount) / previousCount * 100) : 0;
      const avgGrowth = previousAvg > 0 ? ((currentAvg - previousAvg) / previousAvg * 100) : 0;
      
      setComparison({
        current_period: {
          total_sales: currentTotal,
          transactions: currentCount,
          average_sale: currentAvg
        },
        previous_period: {
          total_sales: previousTotal,
          transactions: previousCount,
          average_sale: previousAvg
        },
        growth: {
          sales_growth: salesGrowth,
          transactions_growth: transGrowth,
          average_growth: avgGrowth
        }
      });
    } catch (error) {
      console.error('Error fetching comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!report) return;
    
    const csv = `Rapport,${report.month}\nTotal des ventes,${report.total_sales}\nNombre de transactions,${report.number_of_transactions}\nMoyenne journalière,${report.daily_average}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_${selectedYear}_${selectedMonth}.csv`;
    a.click();
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(num));
  };

  useEffect(() => {
    if (reportType === 'monthly') {
      fetchReport();
    } else {
      fetchComparison();
    }
  }, [selectedMonth, selectedYear, reportType, periodDays]);

  if (loading) {
    return <LoadingSpinner message="Chargement du rapport..." />;
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h2>📊 Rapports & Analyses</h2>
        <div className="report-type-selector">
          <button 
            className={reportType === 'monthly' ? 'btn-active' : 'btn-ghost'}
            onClick={() => setReportType('monthly')}
          >
            Rapport Mensuel
          </button>
          <button 
            className={reportType === 'comparison' ? 'btn-active' : 'btn-ghost'}
            onClick={() => setReportType('comparison')}
          >
            Comparaison Périodes
          </button>
        </div>
      </div>

      {reportType === 'monthly' ? (
        <>
          <div className="report-filters">
            <div className="filter-group">
              <label>Mois:</label>
              <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                {[...Array(12)].map((_, i) => (
                  <option key={i} value={i + 1}>
                    {new Date(2000, i).toLocaleString('fr', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Année:</label>
              <input
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                min="2020"
                max="2030"
              />
            </div>
            {report && (
              <button className="btn-export" onClick={exportToCSV}>
                📥 Exporter CSV
              </button>
            )}
          </div>

          {report ? (
            <div className="report-details">
              <div className="report-header-info">
                <h3>📅 {report.month}</h3>
              </div>
              
              <div className="report-metrics">
                <div className="metric-card primary">
                  <div className="metric-icon">💰</div>
                  <div className="metric-content">
                    <div className="metric-label">Chiffre d'affaires</div>
                    <div className="metric-value">{formatNumber(report.total_sales)} CFA</div>
                  </div>
                </div>
                
                <div className="metric-card secondary">
                  <div className="metric-icon">🛒</div>
                  <div className="metric-content">
                    <div className="metric-label">Transactions</div>
                    <div className="metric-value">{report.number_of_transactions}</div>
                  </div>
                </div>
                
                <div className="metric-card accent">
                  <div className="metric-icon">📈</div>
                  <div className="metric-content">
                    <div className="metric-label">Moyenne journalière</div>
                    <div className="metric-value">{formatNumber(report.daily_average)} CFA</div>
                  </div>
                </div>

                <div className="metric-card info">
                  <div className="metric-icon">💳</div>
                  <div className="metric-content">
                    <div className="metric-label">Panier moyen</div>
                    <div className="metric-value">
                      {formatNumber(report.number_of_transactions > 0 ? report.total_sales / report.number_of_transactions : 0)} CFA
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="empty-message">Aucun rapport disponible pour cette période</p>
          )}
        </>
      ) : (
        <>
          <div className="report-filters">
            <div className="filter-group">
              <label>Période de comparaison:</label>
              <select value={periodDays} onChange={(e) => setPeriodDays(Number(e.target.value))}>
                <option value={7}>7 derniers jours</option>
                <option value={14}>14 derniers jours</option>
                <option value={30}>30 derniers jours</option>
                <option value={60}>60 derniers jours</option>
                <option value={90}>90 derniers jours</option>
              </select>
            </div>
          </div>

          {comparison && (
            <div className="comparison-container">
              <div className="comparison-header">
                <h3>📊 Comparaison: {periodDays} derniers jours vs période précédente</h3>
              </div>

              <div className="comparison-grid">
                <div className="comparison-section current">
                  <h4>Période actuelle</h4>
                  <div className="comparison-stats">
                    <div className="comparison-stat">
                      <span className="stat-label">Ventes totales</span>
                      <span className="stat-value">{formatNumber(comparison.current_period.total_sales)} CFA</span>
                    </div>
                    <div className="comparison-stat">
                      <span className="stat-label">Transactions</span>
                      <span className="stat-value">{comparison.current_period.transactions}</span>
                    </div>
                    <div className="comparison-stat">
                      <span className="stat-label">Vente moyenne</span>
                      <span className="stat-value">{formatNumber(comparison.current_period.average_sale)} CFA</span>
                    </div>
                  </div>
                </div>

                <div className="comparison-section previous">
                  <h4>Période précédente</h4>
                  <div className="comparison-stats">
                    <div className="comparison-stat">
                      <span className="stat-label">Ventes totales</span>
                      <span className="stat-value">{formatNumber(comparison.previous_period.total_sales)} CFA</span>
                    </div>
                    <div className="comparison-stat">
                      <span className="stat-label">Transactions</span>
                      <span className="stat-value">{comparison.previous_period.transactions}</span>
                    </div>
                    <div className="comparison-stat">
                      <span className="stat-label">Vente moyenne</span>
                      <span className="stat-value">{formatNumber(comparison.previous_period.average_sale)} CFA</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="growth-indicators">
                <h4>📈 Évolution</h4>
                <div className="growth-cards">
                  <div className={`growth-card ${comparison.growth.sales_growth >= 0 ? 'positive' : 'negative'}`}>
                    <div className="growth-icon">
                      {comparison.growth.sales_growth >= 0 ? '📈' : '📉'}
                    </div>
                    <div className="growth-content">
                      <div className="growth-label">Chiffre d'affaires</div>
                      <div className="growth-value">
                        {comparison.growth.sales_growth >= 0 ? '+' : ''}{comparison.growth.sales_growth.toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  <div className={`growth-card ${comparison.growth.transactions_growth >= 0 ? 'positive' : 'negative'}`}>
                    <div className="growth-icon">
                      {comparison.growth.transactions_growth >= 0 ? '📈' : '📉'}
                    </div>
                    <div className="growth-content">
                      <div className="growth-label">Transactions</div>
                      <div className="growth-value">
                        {comparison.growth.transactions_growth >= 0 ? '+' : ''}{comparison.growth.transactions_growth.toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  <div className={`growth-card ${comparison.growth.average_growth >= 0 ? 'positive' : 'negative'}`}>
                    <div className="growth-icon">
                      {comparison.growth.average_growth >= 0 ? '📈' : '📉'}
                    </div>
                    <div className="growth-content">
                      <div className="growth-label">Panier moyen</div>
                      <div className="growth-value">
                        {comparison.growth.average_growth >= 0 ? '+' : ''}{comparison.growth.average_growth.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;